import React from 'react';
import { MapPin, Phone, Mail, Clock, Facebook, Instagram, Twitter, Youtube, Star, ExternalLink } from 'lucide-react';
import { useParams } from 'react-router-dom';

const LandingPublicPreview = () => {
  const { restaurantId } = useParams();
  
  // Cargar config DIRECTAMENTE sin hooks complicados
  const getConfig = () => {
    try {
      const saved = localStorage.getItem(`kumia_landing_${restaurantId || 'demo-restaurant'}`);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error('Error loading config:', error);
    }
    
    // Config por defecto
    return {
      theme: { 
        primaryColor: '#7AC943', 
        secondaryColor: '#2d2d2d', 
        textColor: '#ffffff', 
        accentColor: '#f59e0b' 
      },
      hero: { 
        enabled: true, 
        restaurantName: 'Mi Restaurante Demo', 
        slogan: 'Bienvenidos a nuestro restaurante',
        logo: '',
        ctaText: 'Ver Menú', 
        ctaLink: '#menu',
        showNavigation: false
      },
      about: { enabled: false },
      menu: { enabled: false },
      reservations: { enabled: false },
      location: { enabled: false, locations: [] },
      contact: { enabled: false, socialMedia: {} },
      reviews: { enabled: false, manualReviews: [] },
      faqs: { enabled: false, questions: [] },
      schedule: { enabled: false, days: [] },
      footer: { 
        enabled: true, 
        copyright: '© 2025 Mi Restaurante',
        customLinks: []
      }
    };
  };
  const config = getConfig();
  const theme = config.theme || { primaryColor: '#7AC943', secondaryColor: '#2d2d2d', textColor: '#ffffff' };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      {config.hero?.enabled && (
        <section 
          className="relative min-h-screen flex items-center justify-center"
          style={{ backgroundColor: theme.secondaryColor }}
        >
          {/* Background image if exists */}
          {config.hero.backgroundImage && (
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${config.hero.backgroundImage})` }}
            />
          )}
          
          <div className="absolute inset-0 bg-black/50"></div>
          
          <div className="relative z-10 text-center px-6 max-w-4xl">
            {config.hero.logo && (
              <img src={config.hero.logo} alt="Logo" className="w-32 h-32 mx-auto mb-6 object-contain" />
            )}
            <h1 className="text-6xl font-bold text-content-primary mb-4">
              {config.hero.restaurantName || 'Mi Restaurante'}
            </h1>
            {config.hero.slogan && (
              <p className="text-2xl text-content-secondary mb-8">
                {config.hero.slogan}
              </p>
            )}
            <a
              href={config.hero.ctaLink || '#menu'}
              className="inline-block px-8 py-4 rounded-lg text-lg font-bold text-content-primary transition-colors hover:opacity-90"
              style={{ backgroundColor: theme.primaryColor }}
            >
              {config.hero.ctaText || 'Ver Menú'}
            </a>
          </div>
        </section>
      )}

      {/* About Section */}
      {config.about?.enabled && config.about.description && (
        <section id="about" className="py-20 px-6 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-6 text-center">
              {config.about.title || 'Acerca de Nosotros'}
            </h2>
            <p 
              className="text-lg text-gray-700 leading-relaxed mb-8 max-w-4xl mx-auto"
              style={{ textAlign: config.about.textAlign || 'center' }}
            >
              {config.about.description}
            </p>

            {config.about.highlights?.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
                {config.about.highlights.map((highlight, index) => (
                  <div key={index} className="bg-white p-6 rounded-lg shadow-md text-center">
                    <p className="text-gray-800 font-medium">{highlight}</p>
                  </div>
                ))}
              </div>
            )}

            {config.about.mediaType === 'images' && config.about.images?.length > 0 && (
              <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
                {config.about.images.filter(img => img).map((img, idx) => (
                  <img key={idx} src={img} alt={`About ${idx + 1}`} className="w-full h-64 object-cover rounded-lg" />
                ))}
              </div>
            )}

            {config.about.mediaType === 'video' && config.about.videoUrl && (
              <div className="mt-12 max-w-4xl mx-auto">
                <video src={config.about.videoUrl} controls className="w-full rounded-lg" />
              </div>
            )}
          </div>
        </section>
      )}

      {/* Reservations Section */}
      {config.reservations?.enabled && (
        <section id="reservations" className="py-20 px-6 text-content-primary" style={{ backgroundColor: theme.secondaryColor }}>
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-4">
              {config.reservations.title || 'Reservar Mesa'}
            </h2>
            <p className="text-xl text-content-secondary mb-8">
              {config.reservations.description || 'Reserva tu mesa con anticipación'}
            </p>
            {config.reservations.requiresPayment && (
              <p className="text-lg mb-6" style={{ color: theme.accentColor }}>
                💳 Pago de compromiso: ${config.reservations.paymentAmount} {config.reservations.paymentCurrency}
              </p>
            )}
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-6 text-left max-w-2xl mx-auto">
              <h4 className="font-bold mb-3">⚠️ Términos de Reserva:</h4>
              <ul className="space-y-2 text-sm text-content-secondary">
                <li>• Re-agendamiento: máximo 3 horas antes de la hora de reserva</li>
                <li>• Canjeable por aperitivos de entrada en el restaurante</li>
                <li>• No reembolsable (excepto cancelación 24h antes)</li>
                <li>• Al reservar, aceptas ser contactado vía email/teléfono/WhatsApp</li>
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* Location Section */}
      {config.location?.enabled && config.location.locations?.length > 0 && (
        <section id="location" className="py-20 px-6 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              {config.location.title || 'Encuéntranos'}
            </h2>
            <div className="space-y-12">
              {config.location.locations.map((loc, idx) => (
                <div key={idx} className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{loc.name}</h3>
                  {loc.address && (
                    <div className="flex items-start gap-3 mb-3">
                      <MapPin style={{ color: theme.primaryColor }} size={20} />
                      <div>
                        <p className="text-gray-800">{loc.address}</p>
                        {loc.city && <p className="text-gray-600">{loc.city}, {loc.country}</p>}
                      </div>
                    </div>
                  )}
                  {loc.phone && (
                    <div className="flex items-center gap-3 mb-3">
                      <Phone style={{ color: theme.primaryColor }} size={20} />
                      <a href={`tel:${loc.phone}`} className="text-gray-800 hover:text-green-600">{loc.phone}</a>
                    </div>
                  )}
                  {loc.image && (
                    <img src={loc.image} alt={loc.name} className="w-full h-64 object-cover rounded-lg my-4" />
                  )}
                  {loc.googleMapsUrl && (
                    <a 
                      href={loc.googleMapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-content-primary mt-4 hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: theme.primaryColor }}
                    >
                      <ExternalLink size={18} />
                      Ver en Google Maps
                    </a>
                  )}
                  {loc.googleMapsEmbed && (
                    <div className="mt-4 h-96 rounded-lg overflow-hidden">
                      <iframe
                        src={loc.googleMapsEmbed}
                        width="100%"
                        height="100%"
                        style={{ border: 0 }}
                        allowFullScreen=""
                        loading="lazy"
                        title={`Mapa de ${loc.name}`}
                      ></iframe>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact Section */}
      {config.contact?.enabled && (
        <section id="contact" className="py-20 px-6 bg-gray-50">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-12">
              {config.contact.title || 'Contacto'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              {config.contact.phone && (
                <div className="flex flex-col items-center">
                  <Phone style={{ color: theme.primaryColor }} className="mb-3" size={32} />
                  <a href={`tel:${config.contact.phone}`} className="text-lg text-gray-800 hover:text-green-600">
                    {config.contact.phone}
                  </a>
                </div>
              )}
              {config.contact.email && (
                <div className="flex flex-col items-center">
                  <Mail style={{ color: theme.primaryColor }} className="mb-3" size={32} />
                  <a href={`mailto:${config.contact.email}`} className="text-lg text-gray-800 hover:text-green-600">
                    {config.contact.email}
                  </a>
                </div>
              )}
              {config.contact.whatsapp && (
                <div className="flex flex-col items-center">
                  <Phone style={{ color: theme.primaryColor }} className="mb-3" size={32} />
                  <a 
                    href={`https://wa.me/${config.contact.whatsapp.replace(/[^0-9]/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-lg text-gray-800 hover:text-green-600"
                  >
                    WhatsApp
                  </a>
                </div>
              )}
            </div>

            {config.contact.socialMedia && (
              <div className="flex justify-center gap-6">
                {config.contact.socialMedia.instagram && (
                  <a href={config.contact.socialMedia.instagram} target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
                    <Instagram className="text-pink-600" size={36} />
                  </a>
                )}
                {config.contact.socialMedia.facebook && (
                  <a href={config.contact.socialMedia.facebook} target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
                    <Facebook className="text-blue-600" size={36} />
                  </a>
                )}
                {config.contact.socialMedia.twitter && (
                  <a href={config.contact.socialMedia.twitter} target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
                    <Twitter className="text-blue-700 dark:text-blue-400" size={36} />
                  </a>
                )}
                {config.contact.socialMedia.youtube && (
                  <a href={config.contact.socialMedia.youtube} target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
                    <Youtube className="text-red-600" size={36} />
                  </a>
                )}
                {config.contact.socialMedia.tripadvisor && (
                  <a href={config.contact.socialMedia.tripadvisor} target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
                    <Star className="text-green-600" size={36} fill="currentColor" />
                  </a>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Reviews Section */}
      {config.reviews?.enabled && config.reviews.manualReviews?.length > 0 && (
        <section className="py-20 px-6 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              {config.reviews.title || 'Testimonios'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {config.reviews.manualReviews.slice(0, 9).map((review, idx) => (
                <div key={idx} className="bg-gray-50 rounded-lg p-6">
                  {review.image && (
                    <img src={review.image} alt={review.name} className="w-full h-48 object-cover rounded-lg mb-4" />
                  )}
                  <div className="flex gap-1 mb-4">
                    {[...Array(review.rating || 5)].map((_, i) => (
                      <Star key={i} className="text-yellow-700 dark:text-yellow-500 fill-current" size={20} />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 italic">&ldquo;{review.text}&rdquo;</p>
                  <p className="font-semibold text-gray-900">{review.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQs Section */}
      {config.faqs?.enabled && config.faqs.questions?.length > 0 && (
        <section className="py-20 px-6 bg-gray-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              {config.faqs.title || 'Preguntas Frecuentes'}
            </h2>
            <div className="space-y-4">
              {config.faqs.questions.map((faq, idx) => (
                <div key={idx} className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">{faq.question}</h3>
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Schedule Section */}
      {config.schedule?.enabled && config.schedule.days?.length > 0 && (
        <section className="py-20 px-6 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              {config.schedule.title || 'Horarios de Atención'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {config.schedule.days.map((day, idx) => (
                <div key={idx} className="flex items-center justify-between bg-gray-50 rounded-lg p-4">
                  <span className="font-semibold text-gray-900">{day.day}</span>
                  <div className="flex items-center gap-2">
                    <Clock style={{ color: theme.primaryColor }} size={18} />
                    {day.closed ? (
                      <span className="text-red-600">Cerrado</span>
                    ) : (
                      <span className="text-gray-700">{day.open} - {day.close}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      {config.footer?.enabled && (
        <footer className="py-12 px-6 text-content-primary" style={{ backgroundColor: theme.secondaryColor }}>
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-6">
              <p className="text-content-muted">{config.footer.copyright || '© 2025 Mi Restaurante'}</p>
            </div>
            
            {(config.footer.showTerms || config.footer.showPrivacy || config.footer.showReservations || config.footer.showMenu || config.footer.customLinks?.length > 0) && (
              <div className="flex flex-wrap justify-center gap-6 text-sm">
                {config.footer.showTerms && (
                  <button className="text-content-muted hover:text-content-primary transition-colors">
                    Términos y Condiciones
                  </button>
                )}
                {config.footer.showPrivacy && (
                  <button className="text-content-muted hover:text-content-primary transition-colors">
                    Política de Privacidad
                  </button>
                )}
                {config.footer.showReservations && (
                  <a href="#reservations" className="text-content-muted hover:text-content-primary transition-colors">
                    Reservar
                  </a>
                )}
                {config.footer.showMenu && (
                  <a href="#menu" className="text-content-muted hover:text-content-primary transition-colors">
                    Ver Menú
                  </a>
                )}
                {config.footer.customLinks?.map((link, idx) => (
                  <a 
                    key={idx}
                    href={link.url}
                    className="text-content-muted hover:text-content-primary transition-colors"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {link.text}
                  </a>
                ))}
              </div>
            )}
          </div>
        </footer>
      )}
    </div>
  );
};

export default LandingPublicPreview;

